import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, HardHat, Edit, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_TYPES = [
  'Excavator', 'Crane', 'Concrete Mixer', 'Bulldozer', 'Loader',
  'Grader', 'Compactor', 'Dump Truck', 'Tower Crane', 'Scaffolding',
  'Generator', 'Welding Machine', 'Other'
];

const EquipmentRegistry = () => {
  const { getAuthHeader } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    equipment_id: '',
    type: '',
    cost_per_hour: 0,
    purchase_date: '',
    status: 'available'
  });

  const fetchEquipment = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/erp/resources/equipment`, {
        headers: getAuthHeader()
      });
      setEquipment(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEquipment) {
        await axios.put(
          `${BACKEND_URL}/api/erp/resources/equipment/${editingEquipment.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/erp/resources/equipment`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      fetchEquipment();
      setShowModal(false);
      setFormData({ name: '', equipment_id: '', type: '', cost_per_hour: 0, purchase_date: '', status: 'available' });
      setEditingEquipment(null);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (equip) => {
    setEditingEquipment(equip);
    setFormData({
      name: equip.name,
      equipment_id: equip.equipment_id,
      type: equip.type,
      cost_per_hour: equip.cost_per_hour,
      purchase_date: equip.purchase_date,
      status: equip.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this equipment?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/erp/resources/equipment/${id}`, {
        headers: getAuthHeader()
      });
      fetchEquipment();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Equipment Registry</h1>
          <p className="text-gray-600">Manage construction equipment</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Equipment
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {equipment.map(equip => (
          <Card key={equip.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-lg">{equip.name}</h3>
                <p className="text-sm text-gray-600">{equip.equipment_id}</p>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  {equip.type}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(equip)}>
                  <Edit size={14} className="text-blue-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(equip.id)}>
                  <Trash2 size={14} className="text-red-600" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${
                  equip.status === 'available' ? 'text-green-600' :
                  equip.status === 'in_use' ? 'text-blue-600' : 'text-orange-600'
                }`}>
                  {equip.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Cost/Hour:</span>
                <span className="font-bold">₹{equip.cost_per_hour}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Hours:</span>
                <span className="font-medium">{equip.total_hours_used || 0} hrs</span>
              </div>
            </div>
          </Card>
        ))}
        {equipment.length === 0 && (
          <Card className="p-12 text-center col-span-full">
            <HardHat size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No equipment registered</p>
          </Card>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingEquipment ? 'Edit Equipment' : 'Add Equipment'}</h2>
              <Button size="sm" variant="ghost" onClick={() => { setShowModal(false); setEditingEquipment(null); }}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Equipment Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Equipment ID</label>
                <input
                  type="text"
                  value={formData.equipment_id}
                  onChange={(e) => setFormData({...formData, equipment_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., EXC-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Type</option>
                  {EQUIPMENT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cost per Hour (₹)</label>
                <input
                  type="number"
                  value={formData.cost_per_hour}
                  onChange={(e) => setFormData({...formData, cost_per_hour: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Date</label>
                <input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="available">Available</option>
                  <option value="in_use">In Use</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingEquipment(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary">
                  {editingEquipment ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentRegistry;